/**
 * @file top-notifications.tsx
 * @module app/modules/dashboard/top-notifications
 * @description Displays the highest-priority notifications in the dashboard.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from 'react';
import { Card, ListGroup, ListGroupItem } from 'react-bootstrap';
import { useWarehouse } from '../../context/WarehouseContext';
import APICallService from '../../../api/apiCallService';
import { AUTH } from '../../../api/apiEndPoints';
import Method from '../../../utils/methods';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Escalation } from '../../../utils/constants';
import { useAuth } from '../auth';
import { useNavigationHistory } from '../../../utils/useNavigationHistory';
import ImageWithFallback from '../../modules/_common/components/image-with-fallback';

/** `onCount` reports how many notifications there are (undefined while loading). */
const TopNotifications = ({ onCount }: { onCount?: (count: number | undefined) => void }) => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const [notification, setNotification] = useState<any>(undefined);
  useEffect(() => {
    (async () => {
      if (!selectedWarehouse?.value) return;
      if (
        currentUser?.roleAndPermission?.roleType == 'R0001' ||
        currentUser?.roleAndPermission?.roleType == 'R0003'
      ) {
        await fetchNotification();
      }
    })();
  }, [selectedWarehouse?.value, currentUser]);
  const fetchNotification = async () => {
    let params: any = {
      pageNo: 1,
      limit: 5,
      sortKey: '_createdAt',
      sortOrder: -1,
      type: [Escalation],
    };
    if (selectedWarehouse?.value) {
      params = { ...params, warehouse: selectedWarehouse.value };
    }
    let apiService = new APICallService(AUTH.notification, params);
    let response = await apiService.callAPI();
    setNotification(response?.records ?? []);
  };
  useEffect(() => {
    onCount?.(notification === undefined ? undefined : notification.length);
  }, [notification, onCount]);
  // Nothing to show: no card at all (the dashboard gives Pending actions the full width)
  if (!notification || notification.length === 0) return null;
  return (
    <>
      {/* Shown for the allowed roles only when there are notifications */}
      {(currentUser?.roleAndPermission?.roleType == 'R0001' ||
        currentUser?.roleAndPermission?.roleType == 'R0003') && (
          <Card className="mb-7 bg-f9f9f9 h-100" data-testid="top-notifications">
            <Card.Header className="border-bottom">
              <div className="card-title">
                <h2 className="fs-22 fw-bolder">Top Notifications</h2>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {notification &&
                notification?.length > 0 &&
                notification?.map((notifVal: any, index: number) => (
                  <>
                    <div
                      key={`alert${index}`}
                      className="d-flex flex-stack px-3 my-4"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (notifVal.type === Escalation) {
                          navigateTo(
                            '/UProcure/escalations/escalation-details',
                            {
                              state: { id: notifVal.metadata.escalationId },
                            }
                          );
                        }
                      }}
                    >
                      <div className="d-flex">
                        <div className="symbol symbol-35px m-2">
                          <span className={clsx('symbol-label', `bg-white`)}>
                            <ImageWithFallback
                              src={notifVal.image}
                              alt="check green"
                              height={40}
                              width={40}
                            />
                          </span>
                        </div>
                        <div className="mb-0 mx-2">
                          <p className="fs-16 text-dark fw-500 mb-0">
                            {notifVal.message}
                          </p>
                          <div className="text-gray fw-500 fs-14">
                            {Method.convertDateToDDMMYYYY(notifVal._createdAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                    {index !== notification.length - 1 && (
                      <div className="separator m-5"></div>
                    )}
                  </>
                ))}
            </Card.Body>
          </Card>
        )}
    </>
  );
};

export default TopNotifications;
